// deps
import React, { ReactElement, memo, useContext, useCallback, useMemo } from "react";

// internal deps
import { PaymentGroupDataDef, MorePaymentOptions, MorePaymentOptionsOtherProps } from "@swiggy-private/payment-ui";

// components
import { ChildrenProps as PaymentActionProps } from "@payment-client/components/show-payments/components/payment-action-provider";

// contexts
import { ShowPaymentContext } from "@payment-client/contexts/ShowPaymentContext";
import { AmazonPaySdkContext } from "@payment-client/contexts/AmazonPaySdkContext";

// interfaces
import { FRICTION_LAYOUT_ID } from "@payment-client/interfaces/PaymentApi";

// helpers
import { getFrictionLayoutId } from "@payment-client/helpers/PaymentHelper";
import { getThreeClickFrictionGroupOptions } from "@payment-client/helpers/MorePayments";

// constants
import { MORE_PAYMENTS_TEXTS } from "@payment-client/constants/MorePayments";

// analytics
import { AnalyticsContext } from "@swiggy-private/analytics";
import {
    onMorePaymentOptionGroupImpressionEvent,
    onMorePaymentOptionGroupTooltipImpressionEvent,
} from "@payment-client/analytics/events/Payment";

export interface FrictionGroupProps {
    frictionGroups: PaymentGroupDataDef[];
    onOptionClick: PaymentActionProps["onMorePaymentsOptionSelect"];
}

function FrictionGroupRenderer(props: FrictionGroupProps): ReactElement | null {
    const { paymentResponse } = useContext(ShowPaymentContext);
    const { impressionEvent } = useContext(AnalyticsContext);
    // apay state
    const { state: amazonPaySdkState } = useContext(AmazonPaySdkContext);

    const frictionLayoutId: FRICTION_LAYOUT_ID = getFrictionLayoutId(paymentResponse);
    const isTwoClickFriction = frictionLayoutId === FRICTION_LAYOUT_ID.TWO_CLICK;

    // analytics event If group is in view of user
    const onViewEnter = useCallback(
        (groupName: string): void => {
            onMorePaymentOptionGroupImpressionEvent(groupName, impressionEvent);
        },
        [impressionEvent],
    );

    const onViewTooltip = useCallback(
        (groupName: string, text: string): void => {
            onMorePaymentOptionGroupTooltipImpressionEvent(groupName, text, impressionEvent);
        },
        [impressionEvent],
    );

    const onOptionClick = useCallback(
        (paymentGroup: PaymentGroupDataDef) => {
            props.onOptionClick(paymentGroup, !isTwoClickFriction ? props.frictionGroups : undefined);
        },
        [props, isTwoClickFriction],
    );
    const otherProps = useMemo(
        () => ({
            amazonPaySdkStateData: amazonPaySdkState,
        }),
        [amazonPaySdkState],
    );

    if (paymentResponse == null || props.frictionGroups.length === 0) {
        return null;
    }

    const title = isTwoClickFriction ? MORE_PAYMENTS_TEXTS.MORE_PAYMENT_OPTIONS : MORE_PAYMENTS_TEXTS.MORE_OPTIONS;
    const paymentGroups = isTwoClickFriction
        ? props.frictionGroups
        : getThreeClickFrictionGroupOptions(paymentResponse.frictionViewMorePaymentSubtext);

    return (
        <MorePaymentOptions
            title={title}
            paymentGroups={paymentGroups}
            onOptionClick={onOptionClick}
            onViewEnter={onViewEnter}
            onViewTooltip={onViewTooltip}
            otherProps={otherProps as MorePaymentOptionsOtherProps}
        />
    );
}

export default memo(FrictionGroupRenderer);
